import React from 'react';
import { classNames } from '../../utils/classNames';

type TableContainerProps = {
  className?: string;
  children: React.ReactNode;
};

export const TableContainer: React.FC<TableContainerProps> = ({ className, children }) => (
  <div
    className={classNames(
      'overflow-hidden rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] shadow-soft',
      className
    )}
  >
    <div className="w-full overflow-x-auto">
      {children}
    </div>
  </div>
);

type TableProps = {
  className?: string;
  children: React.ReactNode;
};

export const Table: React.FC<TableProps> = ({ className, children }) => (
  <table className={classNames('min-w-[640px] max-w-full divide-y divide-[var(--app-border)] md:min-w-full', className)}>
    {children}
  </table>
);

export const TableHead: React.FC<TableProps> = ({ className, children }) => (
  <thead className={classNames('bg-[var(--app-surface-subtle)] text-xs uppercase text-[var(--app-text-muted)]', className)}>{children}</thead>
);

export const TableBody: React.FC<TableProps> = ({ className, children }) => (
  <tbody className={classNames('divide-y divide-[var(--app-border)] bg-[var(--app-surface)]', className)}>{children}</tbody>
);

type TableRowProps = React.HTMLAttributes<HTMLTableRowElement>;
export const TableRow: React.FC<TableRowProps> = ({ className, children, ...props }) => (
  <tr
    className={classNames(
      'transition hover:bg-[var(--app-hover)] focus-within:bg-[var(--app-hover)] focus:outline-none',
      className
    )}
    {...props}
  >
    {children}
  </tr>
);

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  numeric?: boolean;
};

type TableHeaderCellProps = React.ThHTMLAttributes<HTMLTableHeaderCellElement> & { numeric?: boolean };

export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({ className, children, numeric, ...props }) => (
  <th
    scope="col"
    className={classNames(
      'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--app-text-muted)] sm:px-6',
      numeric ? 'text-right' : 'text-left',
      className
    )}
    {...props}
  >
    {children}
  </th>
);

export const TableCell: React.FC<TableCellProps> = ({ className, children, numeric, ...props }) => (
  <td
    className={classNames(
      'px-4 py-3 align-middle text-sm text-[var(--app-text)] sm:px-6 sm:py-4',
      numeric ? 'text-right' : 'text-left',
      className
    )}
    {...props}
  >
    {children}
  </td>
);

export default Table;

